import { Autocomplete, Box, TextField, Checkbox, styled } from "@mui/material";
import { Form, Formik } from "formik";
import FormControlLabel from '@mui/material/FormControlLabel';
import * as Yup from "yup";
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import AuditInfoLabel from "../../../components/AuditInfoLabel";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  gap: "25px",
  justifyContent: "normal!important"
}));

const StyledTextField = styled(TextField)(() => ({
  width: "30rem"
}));

const ExternalMethodForm = ({
   externalMethodData,
   externalLabs = [],
   externalFacilityMethods = [],
   unitOfMeasureList = [],
   existingMethodExternalLabs = [],
   selectedExternalLab,
   selectedExternalFacilityMethod,
   isSaving = false,
   onSave,
   onCancel
  }) => {

  const isAddExternalMethod = !externalMethodData;

  const validationSchema = Yup.object({
    laboratoryName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Laboratory Name is required'),
    methodName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Method Name is required')
      .test('unique-check', 'External Method assignment already exists', function (value) {
        const { laboratoryName } = this.parent;
        if (!isAddExternalMethod) return true;
        return value && laboratoryName ? !existingMethodExternalLabs.some((lab) => 
          lab.methodName === value && lab.laboratoryName === laboratoryName) : true;
    })
  });

  const defaultNewlaboratoryName = isAddExternalMethod ? selectedExternalLab?.laboratoryName ?? '' : '';
  const defaultNewMethodName = isAddExternalMethod ? selectedExternalFacilityMethod?.methodName ?? '' : '';
  const defaultExternalMethodAssigned = defaultNewlaboratoryName && defaultNewMethodName;

  const initialValues = {
    methodFacilityID: externalMethodData?.methodFacilityID ?? 0,
    laboratoryName: externalMethodData?.laboratoryName ?? defaultNewlaboratoryName,
    methodName: externalMethodData?.methodName ?? defaultNewMethodName,
    testFacilityAbv: externalMethodData?.testFacilityAbv ?? 'EXT',
    isActive: externalMethodData?.isActive ?? true,
    isAccreditedTest: externalMethodData?.isAccreditedTest ?? false,
    isMETP: externalMethodData?.isMETP ?? true,
    testCostUSD: externalMethodData?.testCostUSD,
    targetTurnaroundInDays: externalMethodData?.targetTurnaroundInDays,
    minimumSampleAmount: externalMethodData?.minimumSampleAmount,
    minimumSampleAmountUoM: externalMethodData?.minimumSampleAmountUoM,
    createdByEmail: externalMethodData?.createdByEmail,
    createdDate: externalMethodData?.createdDate,
    lastModifiedByEmail: externalMethodData?.lastModifiedByEmail,
    lastModifiedDate: externalMethodData?.lastModifiedDate
  };
  

  const handleCancelClick = () => onCancel?.();  
  const handleSubmit = (values) => onSave?.(values, isAddExternalMethod);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleBlur, errors, setFieldValue, touched, setFieldTouched, isValid, dirty }) => (
        <Form style={{marginTop:"10px"}}>
          
          <StyledBox style={{marginBottom: "30px"}}>

            <Autocomplete
              noOptionsText={"No External Laboratories found"}
              id="externallabs-select"
              value={values.laboratoryName}
              onChange={(e, val) => setFieldValue('laboratoryName', val)}
              onBlur={() => setFieldTouched('laboratoryName', true)}
              disablePortal
              disabled={!isAddExternalMethod || externalLabs.length === 0}
              options={externalLabs ? externalLabs.map((s) => s.laboratoryName) : []}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField 
                {...params} 
                name="laboratoryName"  variant="outlined" size="small" margin="none" 
                label="Laboratory Name"
                error={touched.laboratoryName && Boolean(errors.laboratoryName)}
                helperText={touched.laboratoryName && errors.laboratoryName}
              />}
              autoSelect                  
            />

          </StyledBox>

          <StyledBox sx={{flexDirection:"row"}}>

            <Autocomplete
              noOptionsText={"No External Methods found"}
              id="externalmethods-select"
              value={values.methodName}
              onChange={(e, val) => setFieldValue('methodName', val)}
              onBlur={() => setFieldTouched('methodName', true)}
              disablePortal
              disabled={!isAddExternalMethod || externalFacilityMethods.length === 0}
              options={externalFacilityMethods ? externalFacilityMethods.map((s) => s.methodName) : []}
              sx={{ width: "23rem" }}
              renderInput={(params) => <TextField 
                {...params} 
                name="methodName"  variant="outlined" size="small" margin="none" 
                label="Method Name"
                error={touched.methodName && Boolean(errors.methodName)}
                helperText={touched.methodName && errors.methodName}
              />}
              autoSelect                  
            />

            <StyledTextField
              fullWidth
              sx={{ width: "10rem" }}
              label="Facility Abbreviation"
              name="testFacilityAbv"
              variant="outlined" size="small"
              value={values.testFacilityAbv}
              disabled
            />

          </StyledBox>

          <StyledBox sx={{ flexDirection: 'row', gap: "40px", marginTop: "30px", marginBottom: "20px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={values.isActive}
                  onChange={handleChange}
                />
              }
              label="Active"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="isAccreditedTest"
                  checked={values.isAccreditedTest}
                  onChange={handleChange}
                />
              }
              label="Accredited Test"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="isMETP"
                  checked={values.isMETP}
                  onChange={handleChange}
                />
              }
              label="METP"
            />

          </StyledBox>

          <StyledBox sx={{ flexDirection: 'row', gap: "40px", marginTop: "30px", marginBottom: "20px" }}>
           <StyledTextField
              fullWidth
              sx={{ width: "16rem" }}
              label="Test Cost ($)"
              type="number"
              name="testCostUSD"
              onChange={handleChange}
              onBlur={handleBlur}  
              variant="outlined" size="small"
              value={values.testCostUSD}
            />

           <StyledTextField
              fullWidth
              sx={{ width: "16rem" }}
              label="Expected Turn Around (days)"
              type="number"
              name="targetTurnaroundInDays"
              onChange={handleChange}
              onBlur={handleBlur}  
              variant="outlined" size="small"
              value={values.targetTurnaroundInDays}
            />

          </StyledBox>

          <StyledBox sx={{ flexDirection: 'row', gap: "40px", marginTop: "30px", marginBottom: "30px" }}>


           <StyledTextField
              fullWidth
              sx={{ width: "16rem" }}
              label="Minimum Sample Amount"
              type="number"
              name="minimumSampleAmount"
              onChange={handleChange}
              onBlur={handleBlur}  
              variant="outlined" size="small"
              value={values.minimumSampleAmount}
            />

            <Autocomplete
              noOptionsText={"No UoM found"}
              id="uom-select"
              value={values.minimumSampleAmountUoM}
              onChange={(e, val) => setFieldValue('minimumSampleAmountUoM', val)}
              onBlur={() => setFieldTouched('minimumSampleAmountUoM', true)}
              disablePortal
              options={unitOfMeasureList ? unitOfMeasureList.map((s) => s.uoMName) : []}
              sx={{ width: "16rem" }}
              renderInput={(params) => <TextField 
                {...params} 
                name="minimumSampleAmountUoM"  variant="outlined" size="small" margin="none" 
                label="Minimum Sample UoM"
              />}
              autoSelect           
              autoHighlight       
            />

          </StyledBox>

          <AuditInfoLabel auditObject={values} />

          <div style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
            <GlobalButton
              type="submit"
              variant="contained"
              disabled={!(defaultExternalMethodAssigned || dirty) || isSaving}>
              {"Save"}
            </GlobalButton>

            <GlobalSecondaryButton
              variant="contained"
              style={{ marginLeft: "40px" }}
              onClick={() => {
                handleCancelClick();
              }}>
              Cancel
            </GlobalSecondaryButton>
          </div>
          
        </Form>
      )}
    </Formik>
  );
};

export default ExternalMethodForm;
